/* /admin/flipbook-pdfs — admin-only page that lists every flipbook record in
   the database and lets Mike download each as a PDF (so he can upload them to
   Heyzine). Also a single "Download All as One Book" button. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Download, ArrowLeft, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BRAND  = "#287F8E";
const GREEN  = "#2E9F3A";
const ORANGE = "#F39A1F";
const WINE   = "#8B2A2A";

export default function FlipbookPdfExportPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/flipbooks")
      .then((r) => setItems(r.data?.flipbooks || []))
      .catch(() => toast.error("Couldn't load flipbooks."))
      .finally(() => setLoading(false));
  }, []);

  if (!user) {
    return <div className="p-8 text-center">Please sign in.</div>;
  }
  if (!user.is_admin) {
    return <div className="p-8 text-center">Admin only.</div>;
  }

  const dlOne = async (slug, title) => {
    if (busy) return;
    setBusy(slug);
    try {
      const resp = await fetch(`${BACKEND}/api/flipbooks/${slug}/pdf`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded "${title}"`);
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    }
    setBusy("");
  };

  const dlAll = async () => {
    if (busy) return;
    setBusy("MASTER");
    toast.info("Building the full book PDF — 60–120 sec…");
    try {
      const resp = await fetch(`${BACKEND}/api/flipbooks/master-book/pdf`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "faitharama-master-book.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded master book PDF");
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    }
    setBusy("");
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5F3EE" }} data-testid="flipbook-pdf-export">
      <header className="px-4 md:px-8 py-6 border-b-2 flex items-center justify-between flex-wrap gap-3" style={{ background: WINE, borderColor: ORANGE, color: "#FFFFFF" }}>
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: ORANGE }}>★ ADMIN · FLIPBOOK PDF EXPORT</div>
          <h1 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight">Grab Your PDFs For Heyzine</h1>
          <p className="text-sm opacity-90 mt-1">Download each book individually, then upload it to your Heyzine account.</p>
        </div>
        <Link to="/dashboard" className="inline-flex items-center gap-2 px-3 py-2 rounded border-2 text-xs uppercase tracking-[0.18em] font-bold text-white" style={{ borderColor: "#FFFFFF55" }}>
          <ArrowLeft className="w-4 h-4"/> Back
        </Link>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-xl border-2 p-4 md:p-5 mb-6 flex items-center justify-between gap-3 flex-wrap" style={{ background: "#FFFFFF", borderColor: GREEN }}>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: GREEN }}>★ ONE PDF, ALL BOOKS COMBINED</div>
            <div className="font-bold text-base mt-0.5" style={{ color: "#0F172A" }}>Master Book (all {items.length} flipbooks)</div>
          </div>
          <button onClick={dlAll} disabled={busy !== ""}
                  data-testid="dl-master"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm uppercase tracking-[0.18em] font-bold text-white shadow disabled:opacity-50"
                  style={{ background: GREEN }}>
            <Download className="w-4 h-4"/> {busy === "MASTER" ? "Building…" : "Download Master Book"}
          </button>
        </div>

        {loading ? (
          <div className="text-center text-foreground/60 py-10">Loading…</div>
        ) : (
          <div className="space-y-2">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: BRAND }}>★ INDIVIDUAL PDFS — {items.length} BOOKS</div>
            {items.map((b) => (
              <div key={b.slug || b.id}
                   data-testid={`flipbook-row-${b.slug}`}
                   className="rounded-lg border bg-white px-4 py-3 flex items-center justify-between gap-3"
                   style={{ borderColor: "#E5E7EB" }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 shrink-0" style={{ color: BRAND }}/>
                    <div className="font-bold truncate" style={{ color: "#0F172A" }}>{b.title || b.slug}</div>
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-foreground/55 mt-0.5">/{b.slug}</div>
                </div>
                <button onClick={() => dlOne(b.slug, b.title || b.slug)}
                        disabled={busy !== ""}
                        data-testid={`dl-${b.slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow disabled:opacity-50"
                        style={{ background: ORANGE }}>
                  <Download className="w-3.5 h-3.5"/> {busy === b.slug ? "…" : "PDF"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
